import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import {
  ClickAwayListener,
  FormHelperText,
  InputAdornment,
  Paper,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppColors } from "../../util/AppColors";

const getOptionKey = (option) =>
  option?.value ?? option?.stockId ?? option?.id ?? option?.name ?? "";

const getOptionLabel = (option) => option?.name ?? "";

const matchesSearch = (option, searchText) => {
  if (!searchText) return true;
  if (option?._searchText?.includes(searchText)) return true;
  return [
    option?.name,
    option?.tradeName,
    option?.productName,
    option?.ingredient,
    option?.serviceName,
    option?.variantName,
  ].some(
    (field) =>
      field != null && String(field).toLowerCase().includes(searchText),
  );
};

const SearchableSelect = ({
  list = [],
  value = null,
  handleChange,
  label = "",
  error = false,
  helperText = "",
  disabled = false,
  maxFilteredOptions = 100,
  backgroundColor = "transparent",
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(getOptionLabel(value));
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    if (!open) {
      setInputValue(getOptionLabel(value));
    }
  }, [value, open]);

  const filteredOptions = useMemo(() => {
    const searchText = inputValue?.trim()?.toLowerCase() ?? "";
    const source = Array.isArray(list) ? list : [];
    const limit = maxFilteredOptions ?? source.length;
    const results = [];
    const selectedKey = getOptionKey(value);

    for (let i = 0; i < source.length && results.length < limit; i += 1) {
      const option = source[i];
      if (!matchesSearch(option, searchText)) continue;
      results.push(option);
    }

    if (
      value &&
      selectedKey !== "" &&
      !results.some((opt) => getOptionKey(opt) === selectedKey) &&
      matchesSearch(value, searchText)
    ) {
      results.unshift(value);
      if (results.length > limit) results.length = limit;
    }

    return results;
  }, [list, inputValue, maxFilteredOptions, value]);

  const selectOption = (option) => {
    handleChange?.(null, option);
    setInputValue(getOptionLabel(option));
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    handleChange?.(null, null);
    setInputValue("");
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (event) => {
    const nextValue = event?.target?.value ?? "";
    setInputValue(nextValue);
    setOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        selectOption(filteredOptions[highlightedIndex]);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setInputValue(getOptionLabel(value));
    }
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
        setInputValue(getOptionLabel(value));
      }}
    >
      <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
        <TextField
          fullWidth
          size="small"
          label={label}
          value={inputValue}
          disabled={disabled}
          error={error}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <CloseIcon
                    sx={{
                      color: "action.active",
                      cursor: disabled ? "default" : "pointer",
                      fontSize: 18,
                      mr: 0.5,
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      clearSelection(event);
                    }}
                  />
                ) : null}
                <ArrowDropDownIcon
                  sx={{
                    color: "action.active",
                    cursor: disabled ? "default" : "pointer",
                    transform: open ? "rotate(180deg)" : "none",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    setOpen((prev) => !prev);
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ background: backgroundColor }}
        />

        {open ? (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  padding: "10px 14px",
                  color: AppColors.gray ?? "#6b7280",
                  fontSize: 14,
                }}
              >
                No options
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionKey = getOptionKey(option);
                const isSelected =
                  value != null && getOptionKey(value) === optionKey;
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={`${optionKey}-${index}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectOption(option);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      padding: "8px 14px",
                      cursor: "pointer",
                      background: isHighlighted
                        ? "#f5f5f5"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                      color: getOptionLabel(option)
                        ?.toLowerCase()
                        ?.includes("(clinic)")
                        ? AppColors.appPrimary
                        : "inherit",
                      fontSize: 14,
                    }}
                  >
                    {getOptionLabel(option)}
                  </div>
                );
              })
            )}
          </Paper>
        ) : null}

        {helperText && !open ? (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};

export default SearchableSelect;
